/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: metrics-dashboard-service.ts
 *
 * Created by randysorensen on 2019-02-19.
 */

function MetricsDashboardServiceConstructor (
    this: JS5Object,
    _: JS5Object,
    $interval: any,
    $q: JS5Object,
    moment: JS5Function,
    AuthFactory: JS5Object,
    DashboardResourceFactory: JS5Object,
    DynamicPropertiesService: JS5Object,
    EndPointService: JS5Object,
    PartnerEditService: JS5Object,
    UtilsService: JS5Object
) {

    let dataRefreshInterval: JS5Object;

    const getAllAvailablePartners = () => {
        let partnersCriteria = {
            includeHidden: true,
            fetchLimit: 0,
            fetchOffset: 0
        };

        return PartnerEditService.getAllPartners(partnersCriteria)
            .then((allPartners: JS5Object) => {
                this.allAvailablePartners = allPartners;
            });
    };

    const createMonthKey = (year: number, month: number): number => {
        //
        // Month key format: YYYYMM
        //

        let yearStr = `${year}`;
        let monthStr = `${month}`;
        if (month < 10) {
            monthStr = `0${monthStr}`;
        }

        return parseInt(`${yearStr}${monthStr}`);
    };

    const createMonthKeyRange = (fromDate: Date, toDate: Date): string[] => {
        //
        // Creates all month keys including and between fromDate
        // and toDate.
        //

        let fromKey = createMonthKey(fromDate.getFullYear(), fromDate.getMonth() + 1);
        let toKey = createMonthKey(toDate.getFullYear(), toDate.getMonth() + 1);

        let monthKeys = [];
        let currentKey = fromKey;
        while (currentKey <= toKey) {
            monthKeys.push(`${currentKey}`);
            if ((currentKey % 100) !== 12) {
                // go to next month
                ++currentKey;
            } else {
                // go back to January
                currentKey -= 11;

                // advance year part
                currentKey += 100;
            }
        }

        return monthKeys;
    };

    const getPerMonthDatasetNames = (metricsData: JS5Object) => {
        //
        // Grabs all of the property names which have a
        // "...PerMonth" suffix and returns them as a list of
        // strings
        //

        // get a list of each "...PerMonth" property
        let metricsDataResultKeys = _.keys(metricsData);
        return _.filter(metricsDataResultKeys, (key: string) => {
            return _.includes(key, 'PerMonth');
        });
    };

    const fillInMissingMonths = (metricsData: JS5Object, fromDate: Date, toDate: Date) => {
        //
        // In each of the "...PerMonth" objects, fill in any
        // missing month sums with an implied 0.
        //

        let perMonthDatasetNames = getPerMonthDatasetNames(metricsData);
        let individualMonthKeys = createMonthKeyRange(fromDate, toDate);

        _.forEach(perMonthDatasetNames, (datasetKey: string) => {
            _.forEach(individualMonthKeys, (monthKey: string) => {
                if (metricsData[datasetKey][monthKey] === undefined) {
                    metricsData[datasetKey][monthKey] = 0;
                }
            });
        });
    };

    const addCurrentMonth = (metricsData: JS5Object, fromDate: Date, toDate: Date) => {
        //
        // If the metrics data includes data for the current month,
        // add a new property for each "...PerMonth" data type
        // which has the current month's data.
        //

        let currentDate = new Date();

        if (toDate.getMonth() === currentDate.getMonth() && toDate.getFullYear() && currentDate.getFullYear()) {
            let perMonthDatasetNames = getPerMonthDatasetNames(metricsData);
            let currentMonthKey = createMonthKey(currentDate.getFullYear(), currentDate.getMonth() + 1);
            _.forEach(perMonthDatasetNames, (perMonthDatasetName: string) => {
                let currentMonthPropertyName = _.replace(perMonthDatasetName, 'PerMonth', 'CurrentMonth');
                metricsData[currentMonthPropertyName] = metricsData[perMonthDatasetName][currentMonthKey];
            });
        }
    };

    const toPrettyNumber = (n: string) => _.round(parseInt(n)).toLocaleString();
    const bytesToTB = (b: number) => b / (10 ** 12);
    const toPrettyTB = (b: string) => _.round(bytesToTB(parseInt(b))).toLocaleString();
    const toPercent = (value: string) => _.round(parseFloat(value) * 100.0);

    const datasetDescriptors = [
        {
            datasetPropertyName: 'packagesDeliveredPerMonth',
            label: 'Packages',
            prettifier: toPrettyNumber
        },
        {
            datasetPropertyName: 'filesDeliveredPerMonth',
            label: 'Files',
            prettifier: toPrettyNumber
        },
        {
            datasetPropertyName: 'hoursDeliveredPerMonth',
            label: 'Hours',
            prettifier: toPrettyNumber
        },
        {
            datasetPropertyName: 'bytesDeliveredPerMonth',
            label: 'Size (TB)',
            prettifier: toPrettyTB
        }];

    const createTooltipData = (metricsData: JS5Object) => {
        let monthKeys = createMonthKeyRange(this.currentCriteria.fromDate, this.currentCriteria.toDate);
        let tooltipData: JS5Object = {};

        _.forEach(monthKeys, (monthKey: string) => {
            let monthKeyAsNumber = parseInt(monthKey);
            let year = _.floor(monthKeyAsNumber / 100);
            let month = monthKeyAsNumber % 100;
            let monthName = UtilsService.constants.MONTHS[month - 1];

            tooltipData[monthKey] = {};
            tooltipData[monthKey].title = `${monthName} ${year}`;
            tooltipData[monthKey].lines = [];
            tooltipData[monthKey].tooltipClass = 'monthly-bar-graph-tooltip';

            _.forEach(datasetDescriptors, (datasetDescriptor: JS5Object) => {
                let value = metricsData[datasetDescriptor.datasetPropertyName][monthKeyAsNumber];
                let prettyValue = datasetDescriptor.prettifier(value);
                tooltipData[monthKey].lines.push(`${datasetDescriptor.label}: ${prettyValue}`);
            });

        });
        return tooltipData;
    };

    const makeDataPretty = (metricsData: JS5Object) => {
        let prettyMetricsData: JS5Object = {
            partnerRatioMap: metricsData.partnerRatioMap
        };

        let numericProperties = ['totalPackagesDelivered', 'totalFilesDelivered', 'totalHoursDelivered',
            'packagesDeliveredCurrentMonth', 'filesDeliveredCurrentMonth', 'hoursDeliveredCurrentMonth'];
        _.forEach(numericProperties, (numericProperty: string) => {
            if (metricsData.hasOwnProperty(numericProperty)) {
                prettyMetricsData[numericProperty] = toPrettyNumber(metricsData[numericProperty]);
            }
        });

        let numericDatasets = ['packagesDeliveredPerMonth', 'filesDeliveredPerMonth', 'hoursDeliveredPerMonth',
                               'bytesDeliveredPerMonth'];
        _.forEach(numericDatasets, (numericDataset: string) => {
            prettyMetricsData[numericDataset] = _.cloneDeep(metricsData[numericDataset]);
        });

        let storageSizeProperties = ['totalBytesDelivered', 'bytesDeliveredCurrentMonth'];
        _.forEach(storageSizeProperties, (storageSizeProperty: string) => {
            if (metricsData.hasOwnProperty(storageSizeProperty)) {
                prettyMetricsData[storageSizeProperty] = metricsData[storageSizeProperty];
                let dstProperty = _.replace(storageSizeProperty, 'bytes', 'terabytes');
                dstProperty = _.replace(dstProperty, 'Bytes', 'Terabytes');
                prettyMetricsData[dstProperty] = toPrettyTB(metricsData[storageSizeProperty]);
            }
        });

        prettyMetricsData.terabytesDeliveredPerMonth = {};
        _.forEach(metricsData.bytesDeliveredPerMonth, (bytes: string, monthKey: string) => {
            prettyMetricsData.terabytesDeliveredPerMonth[monthKey] = bytesToTB(parseInt(bytes));
        });

        prettyMetricsData.tooltipData = createTooltipData(prettyMetricsData);
        return prettyMetricsData;
    };

    const addPartnerRatioMap = (metricsData: JS5Object) => {
        _.forEach(metricsData.partnerRatioMap, (value: string, key: string) => {
            metricsData.partnerRatioMap[key] = toPercent(value);
        });
    };

    const getMetricsQueryParams = () => {
        let criteria = this.currentCriteria;

        let fromDateParam = moment(criteria.fromDate).format('MMM DD YYYY');
        let toDateParam = moment(criteria.toDate).format('MMM DD YYYY');

        let queryParams: JS5Object = {
            fromDate: fromDateParam,
            toDate: toDateParam
        };

        if (criteria.partners.length) {
            queryParams.partnerId = _.map(criteria.partners, 'id');
        } else if (criteria.partnerTypes.length) {
            queryParams.partnerType = criteria.partnerTypes;
        }

        const dashboardApiKey = '838f7278-626c-4964-9fcd-ca2359b2076e';
        if (this.chromeless) {
            queryParams.apiKey = dashboardApiKey;
        }

        return queryParams;
    };

    const downloadFile = (url: string) => {
        window.open(url);
    };

    this.exportDeliveryMetrics = () => {
        let queryParams = getMetricsQueryParams();
        let exportURL = `${EndPointService.dashboardEndpoint}/exportDeliveryMetrics`;
        let queryParamCount = 0;

        const addParam = (param: string) => {
            if (queryParamCount === 0) {
                exportURL += '?';
            } else {
                exportURL += '&';
            }
            exportURL += param;
            ++queryParamCount;
        };

        addParam(`fromDate=${queryParams.fromDate.split(' ').join('+')}`);
        addParam(`toDate=${queryParams.toDate.split(' ').join('+')}`);
        if (queryParams.partnerType) {
            _.forEach(queryParams.partnerType, (partnerType: string) => {
                addParam(`partnerType=${partnerType}`);
            });
        }
        if (queryParams.partnerId) {
            _.forEach(queryParams.partnerId, (partnerId: string) => {
                addParam(`partnerId=${partnerId}`);
            });
        }

        addParam(AuthFactory.getAuthQueryString());

        console.info(`opening report link: ${exportURL}`);
        downloadFile(exportURL);
    };

    const createInitialData = (): JS5Object => {
        return {
            partnerRatioMap: {
                Other: 1
            },
            totalPackagesDelivered: 0,
            totalFilesDelivered: 0,
            totalHoursDelivered: 0,
            totalBytesDelivered: 0,
            packagesDeliveredPerMonth: {},
            filesDeliveredPerMonth: {},
            hoursDeliveredPerMonth: {},
            bytesDeliveredPerMonth: {}
        }
    };

    const processMetricsData = (metricsData: JS5Object) => {
        let criteria = this.currentCriteria;

        fillInMissingMonths(metricsData, criteria.fromDate, criteria.toDate);
        addCurrentMonth(metricsData, criteria.fromDate, criteria.toDate);
        metricsData = makeDataPretty(metricsData);
        addPartnerRatioMap(metricsData);

        metricsData.timeStamp = new Date();

        this.metricsData = metricsData;

        _.forEach(this.dataUpdateCallbacks, (callback: JS5Function) => {
            callback(metricsData);
        });

        return metricsData;
    };

    this.getMetricsData = () => {
        let queryParams = getMetricsQueryParams();

        if (!_.keys(this.metricsData).length) {
            let initialPromise = $q.when(processMetricsData(createInitialData()));
            this.getMetricsData();
            return initialPromise;
        }

        return DashboardResourceFactory.getDeliveryMetrics(queryParams)
            .$promise.then((metricsData: JS5Object) => {
                return processMetricsData(metricsData);
            });
    };

    const onAutomaticDataRefresh = () => {
        if (_.isEqual(this.defaultCriteria, this.currentCriteria)) {
            // keep the current criteria up-to-date
            this.setDefaultCriteria();
        } else {
            this.getMetricsData();
        }
    };

    this.activateDataRefreshInterval = () => {
        let refreshInterval = DynamicPropertiesService.getPropValueAsInt('metrics.refreshInterval', 5*60);
        return this.getMetricsData().then((metricsData: JS5Object) => {
            if (dataRefreshInterval === undefined) {
                dataRefreshInterval = $interval(onAutomaticDataRefresh, refreshInterval * 1000);
            }

            return metricsData;
        });
    };

    this.deactivateDataRefreshInterval = () => {
        if (dataRefreshInterval) {
            $interval.cancel(dataRefreshInterval);
        }
        dataRefreshInterval = undefined;
    };

    this.restartDataRefreshInterval = () => {
        this.deactivateDataRefreshInterval();
        return this.activateDataRefreshInterval();
    };

    this.registerDataUpdateCallback = (callback: JS5Function) => {
        if (typeof(callback) === 'function') {
            if (!_.includes(this.dataUpdateCallbacks, callback)) {
                this.dataUpdateCallbacks.push(callback);
            }

            // prime it with data now
            callback(this.metricsData);
        }
    };

    this.refreshNow = () => {
        if (dataRefreshInterval) {
            return this.restartDataRefreshInterval();
        } else {
            return this.getMetricsData();
        }
    };

    this.unregisterDataUpdateCallback = (callback: JS5Function) => {
        _.pull(this.dataUpdateCallbacks, callback);
    };

    this.setCurrentCriteria = (currentCriteria: JS5Object) => {
        this.currentCriteria = _.cloneDeep(currentCriteria);
        return this.refreshNow();
    };

    this.getCurrentCriteria = (): JS5Object => {
        return _.cloneDeep(this.currentCriteria);
    };

    this.createDefaultCriteria = () => {
        let defaultCriteria: JS5Object = {
            fromDate: new Date(),
            toDate: new Date(),
            partners: []
        };

        //
        // Since we're only concerned with months & years, we set
        // the dates to the 10th & 20th of the applicable months
        // to avoid hitting any timezone boundaries, and also to
        // ensure the toDate is always after the fromDate when
        // they fall in the same month
        //

        // by default, start a year ago next month
        defaultCriteria.fromDate.setFullYear(defaultCriteria.fromDate.getFullYear() - 1);
        defaultCriteria.fromDate.setMonth(defaultCriteria.fromDate.getMonth() + 1);
        defaultCriteria.fromDate.setDate(10);

        // by default, include the most recent data
        defaultCriteria.toDate.setDate(20);

        // select all partners & partner types by default - this is
        // interpreted as all partners and all partner types
        defaultCriteria.partners = [];
        defaultCriteria.partnerTypes = [];

        this.defaultCriteria = defaultCriteria;
        return _.cloneDeep(defaultCriteria);
    };

    this.setDefaultCriteria = () => {
        this.createDefaultCriteria();
        this.setCurrentCriteria(this.defaultCriteria);
    };

    this.currentCriteriaIncludesPresent = (): boolean => {
        return (
            (new Date()).getMonth() === _.invoke(this, 'currentCriteria.toDate.getMonth') &&
            (new Date()).getFullYear() === _.invoke(this, 'currentCriteria.toDate.getFullYear')
        );
    };

    this.getAllAvailablePartners = () => {
        return _.cloneDeep(this.allAvailablePartners);
    };

    this.initialize = (chromeless: boolean=false) => {
        this.chromeless = chromeless;

        this.dataUpdateCallbacks = [];
        this.metricsData = {};
        this.allAvailablePartners = [];

        this.defaultCriteria = {};
        this.currentCriteria = {};

        let defaultCriteria = this.createDefaultCriteria();
        this.defaultCriteria = _.cloneDeep(defaultCriteria);
        this.currentCriteria = _.cloneDeep(defaultCriteria);

        if (!chromeless) {
            return $q.all([
                DynamicPropertiesService.queryForProperties(),
                getAllAvailablePartners()
            ]).then(() => {
                return this.activateDataRefreshInterval();
            });
        } else {
            return DynamicPropertiesService.queryForProperties()
                .then(this.activateDataRefreshInterval);
        }
    };

    this.shutdown = () => {
        //
        // TODO
        //
    }

}

angular
    .module('prism-ui.metricsDashboard')
    .service(
        'MetricsDashboardService',
        MetricsDashboardServiceConstructor
    );
